/* eslint-disable no-param-reassign */
import { OkrObjective, OkrKeyResult } from "server/models"
import { ObjectiveNested, KeyResultNested } from "client/store"

type ObjectivesGroupedByObjectiveType = { [parentObjectiveId: number]: OkrObjective[] }
type ObjectivesGroupedByKeyResultType = { [parentKeyResultId: number]: OkrObjective[] }
const GroupObjectivesByParent = (objectives: OkrObjective[]) => {
    const objectivesGroupedByObjective: ObjectivesGroupedByObjectiveType = {}
    const objectivesGroupedByKeyResult: ObjectivesGroupedByKeyResultType = {}
    objectives.forEach((objective) => {
        const { objectiveId, keyResultId } = objective

        if (objectiveId) {
            if (!objectivesGroupedByObjective[objectiveId]) objectivesGroupedByObjective[objectiveId] = []
            objectivesGroupedByObjective[objectiveId].push(objective)
        }
        if (keyResultId) {
            if (!objectivesGroupedByKeyResult[keyResultId]) objectivesGroupedByKeyResult[keyResultId] = []
            objectivesGroupedByKeyResult[keyResultId].push(objective)
        }
        if (!objectiveId && !keyResultId) {
            if (!objectivesGroupedByObjective[0]) objectivesGroupedByObjective[0] = []
            objectivesGroupedByObjective[0].push(objective)
        }
    })
    return { objectivesGroupedByObjective, objectivesGroupedByKeyResult }
}

type KeyResultsGroupedByObjectiveType = { [parentObjectiveId: number]: OkrKeyResult[] }
const GroupKeyResultsByParent = (keyResults: OkrKeyResult[]) => {
    const keyResultsGroupedByObjective: KeyResultsGroupedByObjectiveType = {}
    keyResults.forEach((keyResult) => {
        const { objectiveId } = keyResult
        if (!keyResultsGroupedByObjective[objectiveId]) keyResultsGroupedByObjective[objectiveId] = []
        keyResultsGroupedByObjective[objectiveId].push(keyResult)
    })
    return keyResultsGroupedByObjective
}

const GetObjectivesNested = (
    parentObjectiveId: number,
    parentKeyResultId: number,
    objectivesGroupedByObjective: ObjectivesGroupedByObjectiveType,
    objectivesGroupedByKeyResult: ObjectivesGroupedByKeyResultType,
    keyResultsGroupedByObjective: KeyResultsGroupedByObjectiveType,
) => {
    const nestedObjectives: ObjectiveNested[] = []
    let childObjectives: OkrObjective[]
    if (parentObjectiveId) childObjectives = objectivesGroupedByObjective[parentObjectiveId]
    if (parentKeyResultId) childObjectives = objectivesGroupedByKeyResult[parentKeyResultId]

    if (childObjectives) {
        childObjectives.forEach((objective) => {
            const nestedObjective: ObjectiveNested = {
                objective,
                nestedKeyResults: GetKeyResultsNested(
                    objective.id,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
                nestedObjectives: GetObjectivesNested(
                    objective.id,
                    null,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
            }
            nestedObjectives.push(nestedObjective)
        })

        // remove used rows
        if (parentObjectiveId) delete objectivesGroupedByObjective[parentObjectiveId]
        if (parentKeyResultId) delete objectivesGroupedByKeyResult[parentKeyResultId]
    }
    return nestedObjectives
}

const GetKeyResultsNested = (
    parentObjectiveId: number,
    objectivesGroupedByObjective: ObjectivesGroupedByObjectiveType,
    objectivesGroupedByKeyResult: ObjectivesGroupedByKeyResultType,
    keyResultsGroupedByObjective: KeyResultsGroupedByObjectiveType,
) => {
    const nestedKeyResults: KeyResultNested[] = []
    const childKeyResults = keyResultsGroupedByObjective[parentObjectiveId]

    if (childKeyResults) {
        childKeyResults.forEach((keyResult) => {
            const nestedKeyResult: KeyResultNested = {
                keyResult,
                nestedObjectives: GetObjectivesNested(
                    null,
                    keyResult.id,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
            }
            nestedKeyResults.push(nestedKeyResult)
        })
    }
    return nestedKeyResults
}

// eslint-disable-next-line import/prefer-default-export
export const PrepareOkrNested = (objectives: OkrObjective[], keyResults: OkrKeyResult[]) => {
    const { objectivesGroupedByObjective, objectivesGroupedByKeyResult } = GroupObjectivesByParent(objectives)
    const keyResultsGroupedByObjective = GroupKeyResultsByParent(keyResults)

    const nestedObjectives: ObjectiveNested[] = []

    Object.keys(objectivesGroupedByObjective).forEach((key) => {
        const parentObjectiveId = key as unknown as number
        // continue if row was added to nested level
        if (!objectivesGroupedByObjective[parentObjectiveId]) return

        // all objectives that were not added to nested levels will be added to the top level
        objectivesGroupedByObjective[parentObjectiveId].forEach((objective) => {
            const nested: ObjectiveNested = {
                objective,
                nestedKeyResults: GetKeyResultsNested(
                    objective.id,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
                nestedObjectives: GetObjectivesNested(
                    objective.id,
                    null,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
            }
            nestedObjectives.push(nested)
        })
    })

    Object.keys(objectivesGroupedByKeyResult).forEach((key) => {
        const parentKeyResultId = key as unknown as number
        // continue if row was added to nested level
        if (!objectivesGroupedByKeyResult[parentKeyResultId]) return

        // all objectives that were not added to nested levels will be added to the top level
        objectivesGroupedByKeyResult[parentKeyResultId].forEach((objective) => {
            const nested: ObjectiveNested = {
                objective,
                nestedKeyResults: GetKeyResultsNested(
                    objective.id,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
                nestedObjectives: GetObjectivesNested(
                    objective.id,
                    null,
                    objectivesGroupedByObjective,
                    objectivesGroupedByKeyResult,
                    keyResultsGroupedByObjective,
                ),
            }
            nestedObjectives.push(nested)
        })
    })

    return nestedObjectives
}
